import { GridItemLayout, GRID_CONSTANTS, BLOCK_MIN_SIZES, ChartReportType, TableReportType } from '../types';

/**
 * Проверяет, являются ли данные layout старым форматом.
 * Старый формат: scaled !== true и w < 100.
 */
export const isOldLayoutFormat = (layoutData: GridItemLayout): boolean => {
  return !layoutData.scaled && (layoutData.w || 1) < 100;
};

/**
 * Возвращает ширину блока по умолчанию в зависимости от типа.
 * Все блоки имеют фиксированную ширину 400px (40 единиц сетки).
 */
export const getDefaultBlockWidth = (
  componentType: string,
  title?: string
): number => {
  // Все блоки имеют фиксированную ширину 400px (40 единиц сетки)
  return 40;
};

/**
 * Возвращает высоту блока по умолчанию в зависимости от типа.
 */
export const getDefaultBlockHeight = (
  componentType: string,
  chartType?: string
): number => {
  switch (componentType) {
    case 'number':
      return 4;
    case 'heading':
      return 5;
    case 'chart':
      return chartType === 'pie' ? 20 : 20;
    case 'table':
      return 20;
    default:
      return 20;
  }
};

/**
 * Мигрирует данные layout из старого формата в новый.
 * Старый формат использовал сетку 80 колонок, новый — 800.
 */
export const migrateLayoutData = (
  layoutData: GridItemLayout,
  componentType: string,
  title?: string
): GridItemLayout => {
  const isOldData = isOldLayoutFormat(layoutData);
  const scaleX = isOldData ? GRID_CONSTANTS.SCALE_X : 1;

  // Вычисляем ширину
  let width = Math.round((layoutData.w || 1) * scaleX);

  // Для старых данных применяем минимальную ширину
  if (isOldData) {
    const defaultWidth = getDefaultBlockWidth(componentType, title);
    width = Math.max(width, defaultWidth);
  }

  // Высота блока — сохраняем из данных
  const height = layoutData.h || getDefaultBlockHeight(componentType);

  // Минимальные размеры зависят от типа блока
  const blockMinSizes = BLOCK_MIN_SIZES[componentType] || {
    minW: GRID_CONSTANTS.MIN_BLOCK_WIDTH,
    minH: GRID_CONSTANTS.MIN_BLOCK_HEIGHT,
  };

  return {
    ...layoutData,
    x: Math.round((layoutData.x || 0) * scaleX),
    w: Math.max(width, blockMinSizes.minW),
    y: layoutData.y || 0,
    h: Math.max(height, blockMinSizes.minH),
    minW: blockMinSizes.minW,
    minH: blockMinSizes.minH,
  };
};

/**
 * Вычисляет позицию Y для нового блока (под всеми существующими).
 */
export const calculateNewBlockY = (
  existingBlocks: Array<{ layoutData: GridItemLayout }>
): number => {
  if (existingBlocks.length === 0) return 0;

  return existingBlocks.reduce((maxY, block) => {
    const blockBottom = (block.layoutData?.y || 0) + (block.layoutData?.h || 0);
    return Math.max(maxY, blockBottom);
  }, 0);
};

/**
 * Определяет тип отчёта из даты блока дашборда.
 * @param date - Дата в формате "type_start_end" (например, "registration_2024-01-01_2024-01-31")
 * @returns Тип отчёта для ReportChart
 */
export const getReportTypeFromDate = (date?: string): ChartReportType => {
  if (!date) return 'registration';

  const parts = date.split('_');
  const type = parts[0];

  switch (type) {
    case 'registration':
    case 'universal':
    case 'reg':
      return 'registration';
    case 'ad':
      return 'ad';
    case 'order':
      return 'order';
    case 'payment':
      return 'payment';
    case 'webinar':
    case 'web':
      return 'webinar';
    case 'visit':
      return 'visit';
    default:
      return 'registration';
  }
};

/**
 * Преобразует тип отчёта из ReportChart в ReportTable.
 */
export const chartToTableReportType = (chartType: ChartReportType): TableReportType => {
  if (chartType === 'registration') return 'universal';
  return chartType;
};

/**
 * Вычисляет высоту графика на основе высоты блока.
 */
export const calculateChartHeight = (
  blockHeight: number,
  headerHeight = 50,
  minHeight = 150
): number => {
  const blockHeightPx =
    blockHeight * GRID_CONSTANTS.CELL_HEIGHT +
    (blockHeight - 1) * GRID_CONSTANTS.MARGIN;
  return Math.max(blockHeightPx - headerHeight, minHeight);
};
